// ═══════════════════════════════════════════════════════════════════════════
// menuPorRol — qué ítems del MENÚ LATERAL ve un rol (14-oct-2026)
// ═══════════════════════════════════════════════════════════════════════════
// ⚠️ VISIBILIDAD DEL MENÚ, NO PERMISO. Ocultar un ítem NO impide abrir su ruta
// por URL ni leer sus datos: eso lo deciden las rules y los callables. Es un
// interino pedido por Mariano (14-oct) para que el consultor vea un menú acotado
// sin tocarle permisos — sacarlos rompía la cuenta `agente-runner` (rol
// CONSULTOR), a SUPERVISOR/OPERACIONES (heredan de CONSULTOR) y el trabajo del
// consultor adentro de las empresas. Lo importa SÓLO Layout.jsx: lo vigila
// `menuPorRol.guard.test.js`, para que nadie lo use como candado.
//
// El dato: `roles/{rolId}.menuItemsVisibles` — lista de `path` de ítems del menú,
// tal cual están en los `menuItems` de src/modules/*/config.js o en los fijos de
// la sección «Milbrands Operaciones» (PATHS_FIJOS_DEL_MENU). Inicio y el pie del
// menú (Mi cuenta, Cerrar sesión) no se filtran nunca. Fuente única del valor:
// infra/menu-por-rol.json, que aplica scripts/menu-por-rol.cjs.
//
//   · ausente → sin filtro. Es el estado de todos los roles y el rollback.
//   · mal formado → sin filtro y aviso en consola. El script valida antes de
//     escribir; si igual llega roto, el menú de hoy es mejor que dejar a alguien
//     sin menú por un typo (no es un control de seguridad).
//   · NO se hereda por `extiende` (los permisos sí): cuenta sólo el doc del rol
//     EFECTIVO del usuario, y sólo si ese doc ES de ese rol — `rolDoc` se carga
//     asíncrono y, al cambiar de rol simulado, puede ser todavía el del anterior
//     (mismo criterio que `esRolLadoMilbrands`, ladoRol.js).
// ═══════════════════════════════════════════════════════════════════════════
import { createLogger } from '@core/lib/logger'

const logger = createLogger('menuPorRol')

export const CAMPO_MENU = 'menuItemsVisibles'

/** Ítems fijos de la sección «Milbrands Operaciones» (Layout.jsx), filtrables como los demás. */
export const PATHS_FIJOS_DEL_MENU = Object.freeze(['/admin', '/documentacion'])

/** ¿Es una lista válida de paths de menú? Exportado: el script valida con la misma regla. */
export function esListaDePaths(valor) {
  return Array.isArray(valor) && valor.every((p) => typeof p === 'string' && p.startsWith('/'))
}

/**
 * PURO. El conjunto de paths que el rol deja ver en el menú, o `null` si no
 * hay que filtrar (sin campo, mal formado, o el doc no es del rol efectivo).
 * @param {object|null} rolDoc  el doc del rol que cargó AuthContext
 * @param {string|null} rolId   el rol EFECTIVO del usuario (userDoc.rolId)
 * @returns {Set<string>|null}
 */
export function menuItemsVisiblesDe(rolDoc, rolId) {
  if (!rolDoc || !rolId) return null
  if ((rolDoc.codigo || rolDoc.id) !== rolId) return null
  const valor = rolDoc[CAMPO_MENU]
  if (valor === undefined || valor === null) return null
  if (!esListaDePaths(valor)) {
    logger.warn('roles.' + CAMPO_MENU + ' mal formado: se ignora y el menú queda sin filtro', { rolId })
    return null
  }
  return new Set(valor)
}

/** PURO. ¿Este path pasa el filtro del rol? Sin filtro (`null`), pasa todo. */
export function pasaElMenu(path, permitidos) {
  return !permitidos || permitidos.has(path)
}

/**
 * PURO. Los ítems de un grupo del menú que pasan el filtro del rol. Separadores
 * y subtítulos quedan (el grupo sin ítems reales ya lo descarta Layout); un ítem
 * con hijos se decide por SU path.
 */
export function filtrarItemsMenu(items, permitidos) {
  if (!permitidos) return items
  return items.filter((it) => {
    if (!it || typeof it !== 'object') return true
    if (it.type === 'divider' || it.type === 'subheader') return true
    return permitidos.has(it.path)
  })
}
